import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { Table, InputNumber, Input, Button, message, Spin, Upload } from 'antd';
import {
  ReloadOutlined,
  EditOutlined,
  SaveOutlined,
  FileDoneOutlined,
  FileSyncOutlined,
  UploadOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { getAuthHeaders } from '../utils/auth';
import { buildAPIURL } from '../config/apiConfig';
import dayjs from 'dayjs';
import { usePermissions } from '../hooks/usePermissions';
import { AuthContext } from '../contexts/AuthContext';
import { exportNhapDiemTemplate, exportBangDiem, importNhapDiemFromExcel, validateImportedScores } from '../utils/excelUtils';

// Các hàm tính toán từ hệ thống cũ
function diemToChuVaThang4(diem: number): { chu: string; thang4: number } {
  if (diem >= 8.5) return { chu: 'A', thang4: 4 };
  if (diem >= 8.0) return { chu: 'B+', thang4: 3.5 };
  if (diem >= 7.0) return { chu: 'B', thang4: 3 };
  if (diem >= 6.5) return { chu: 'C+', thang4: 2.5 };
  if (diem >= 5.5) return { chu: 'C', thang4: 2 };
  if (diem >= 5.0) return { chu: 'D+', thang4: 1.5 };
  if (diem >= 4.0) return { chu: 'D', thang4: 1 };
  return { chu: 'F', thang4: 0 };
}

function tinhDiemTBHeSo(diemTX: number | null, diemDK: number | null): number {
  if (typeof diemTX !== 'number' || isNaN(diemTX)) diemTX = 0;
  if (typeof diemDK !== 'number' || isNaN(diemDK)) diemDK = 0;
  return Math.round(((diemTX + diemDK * 2) / 3) * 10) / 10;
}

function tinhDiemTBMon(diemTBHeSo: number, diemThiL1: number | null, diemThiL2: number | null): number {
  if (typeof diemThiL1 !== 'number' || isNaN(diemThiL1)) return 0;

  let tbMon = Math.round((diemTBHeSo * 0.4 + diemThiL1 * 0.6) * 10) / 10;
  if (tbMon < 4 && typeof diemThiL2 === 'number' && !isNaN(diemThiL2)) {
    tbMon = Math.round((diemTBHeSo * 0.4 + diemThiL2 * 0.6) * 10) / 10;
  }
  return tbMon;
}

interface StudentGrade {
  key: string;
  MaHS?: string;
  MaSV?: string;
  HoTen: string;
  NgaySinh: string;
  DiemTX: number | null;
  DiemDK: number | null;
  DiemThiL1: number | null;
  DiemThiL2: number | null;
  DiemTB_HeSo: number;
  DiemTB_Mon: number;
  DiemChu: string;
  DiemThang4: number;
  GhiChu: string;
  TenLopHP?: string;
  TenMonHoc?: string;
  TenGV?: string;
  LopHocPhanID?: string;
}

interface NhapDiemLopHocPhanPageProps {
  lopHocPhanId: string;
  tenKhoaHoc?: string;
  tenMonHoc?: string;
  tenLopHocPhan?: string;
  tenGV?: string;
}

const NhapDiemLopHocPhanPage: React.FC<NhapDiemLopHocPhanPageProps> = ({
  lopHocPhanId,
  tenKhoaHoc = '',
  tenMonHoc = '',
  tenLopHocPhan = '',
  tenGV = ''
}) => {
  const [data, setData] = useState<StudentGrade[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editL1, setEditL1] = useState(false);
  const [editL2, setEditL2] = useState(false);
  const [editTXDK, setEditTXDK] = useState(false);
  const inputRefs = useRef<{ [key: string]: any }>({});
  const colOrder = ['DiemTX', 'DiemDK', 'DiemThiL1', 'DiemThiL2', 'GhiChu'];

  const { hasPermission } = usePermissions();
  const { user } = useContext(AuthContext) || {};

  // Debug permissions
  console.log('[DEBUG] User:', user);
  console.log('[DEBUG] Has GRADE_INPUT_MODULE view:', hasPermission('GRADE_INPUT_MODULE', 'view'));
  console.log('[DEBUG] Has GRADE_INPUT_MODULE edit:', hasPermission('GRADE_INPUT_MODULE', 'edit'));
  console.log('[DEBUG] Has GRADE_INPUT_MODULE all:', hasPermission('GRADE_INPUT_MODULE', 'all'));

  // Kiểm tra quyền nhập điểm cho lớp học phần
  const checkPermissionForClass = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(buildAPIURL(`/lophocphan/${encodeURIComponent(lopHocPhanId)}/check-permission`), {
        headers: headers
      });

      if (response.status === 403) {
        message.error('Bạn không có quyền nhập điểm cho lớp học phần này!');
        return false;
      }

      if (!response.ok) {
        console.error('Permission check failed:', response.status);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking permission:', error);
      message.error('Không thể kiểm tra quyền. Vui lòng thử lại!');
      return false;
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[DEBUG] Fetching data for lopHocPhanId:', lopHocPhanId);

      // Lấy token từ localStorage
      const headers = getAuthHeaders();
      console.log('[DEBUG] Headers for fetchData:', headers);

      // Lấy danh sách sinh viên
      const svRes = await fetch(buildAPIURL(`/lophocphan-sinhvien/${encodeURIComponent(lopHocPhanId)}/sinhvien/full`), {
        headers: headers
      });
      const svData = await svRes.json();
      console.log('[DEBUG] Students API response:', svData);

      const students = svData.success && Array.isArray(svData.data) ? svData.data :
        (svData.data && Array.isArray(svData.data) ? svData.data :
          (Array.isArray(svData) ? svData : []));

      console.log('[DEBUG] Students array:', students);

      // Lấy danh sách điểm đã lưu từ bảng LopHocPhan_SinhVien_Diem
      const diemRes = await fetch(buildAPIURL(`/grades?LopHocPhanID=${encodeURIComponent(lopHocPhanId)}`), {
        headers: headers
      });
      const diemData = await diemRes.json();
      console.log('[DEBUG] Grades API response:', diemData);

      const diemList = diemData.success && Array.isArray(diemData.data) ? diemData.data :
        (diemData.data && Array.isArray(diemData.data) ? diemData.data :
          (Array.isArray(diemData) ? diemData : []));

      console.log('[DEBUG] Grades array:', diemList);

      // Gộp điểm vào danh sách sinh viên
      const data = students.map((sv: any) => {
        const diem = diemList.find((d: any) => {
          const diemMaHS = (d.MaHS || '').toString().trim();
          const svMaHS = (sv.MaHS || sv.MaSV || '').toString().trim();
          console.log('[DEBUG] Comparing diem MaHS:', diemMaHS, 'with sv MaHS:', svMaHS);
          return diemMaHS === svMaHS;
        });

        console.log('[DEBUG] Found diem for student:', sv.MaHS || sv.MaSV, ':', diem);

        const diemTBHeSo = diem ? tinhDiemTBHeSo(diem.DiemTX, diem.DiemDK) : 0;
        const diemTBMon = diem ? tinhDiemTBMon(diemTBHeSo, diem.DiemThiL1, diem.DiemThiL2) : 0;
        const quydoi = diemTBMon > 0 ? diemToChuVaThang4(diemTBMon) : { chu: '', thang4: 0 };

        return {
          ...sv,
          DiemTX: diem ? diem.DiemTX : null,
          DiemDK: diem ? diem.DiemDK : null,
          DiemThiL1: diem ? diem.DiemThiL1 : null,
          DiemThiL2: diem ? diem.DiemThiL2 : null,
          DiemTB_HeSo: diemTBHeSo,
          DiemTB_Mon: diemTBMon,
          DiemChu: quydoi.chu,
          DiemThang4: quydoi.thang4,
          GhiChu: diem ? diem.GhiChu : '',
          key: sv.MaHS || sv.MaSV
        };
      });

      // Sắp xếp theo MaHS tăng dần
      const sortedData = data.sort((a: any, b: any) => {
        const maA = (a.MaHS || a.MaSV || '').toString();
        const maB = (b.MaHS || b.MaSV || '').toString();
        return maA.localeCompare(maB);
      });

      console.log('[DEBUG] Final sorted data:', sortedData);
      setData(sortedData);
    } catch (err) {
      console.error('[DEBUG] Error fetching data:', err);
      message.error('Không lấy được danh sách sinh viên hoặc điểm');
      setData([]);
    }
    setLoading(false);
  }, [lopHocPhanId]);

  useEffect(() => {
    if (lopHocPhanId) {
      fetchData();
    }
  }, [lopHocPhanId, fetchData]);

  const handleInputKeyDown = (e: React.KeyboardEvent, rowIdx: number, field: string) => {
    // Nếu là phím mũi tên thì luôn di chuyển focus
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key)) {
      e.preventDefault();
      const currKey = data[rowIdx]?.MaHS || data[rowIdx]?.MaSV;
      if (!currKey) return;

      let nextRow = rowIdx, nextCol = field;

      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        nextRow = rowIdx + 1;
      }
      if (e.key === 'ArrowUp') {
        nextRow = rowIdx - 1;
      }
      if (e.key === 'ArrowRight') {
        let idx = colOrder.indexOf(field);
        if (idx < colOrder.length - 1) nextCol = colOrder[idx + 1];
      }
      if (e.key === 'ArrowLeft') {
        let idx = colOrder.indexOf(field);
        if (idx > 0) nextCol = colOrder[idx - 1];
      }

      const nextKey = data[nextRow]?.MaHS || data[nextRow]?.MaSV;
      if (nextKey && inputRefs.current[`${nextCol}_${nextKey}`]) {
        inputRefs.current[`${nextCol}_${nextKey}`].focus();
      }
    }
  };

  /* Quyền nhập điểm:
     - Giảng viên được phân công: Chỉ được nhập điểm TX, ĐK (đã check ở checkPermissionForClass)
     - Khảo thí/Admin: Được nhập tất cả (cần check thêm quyền module)
  */

  // Kiểm tra xem user có phải là admin/khảo thí không (để nhập điểm thi)
  const canInputExamGrades = useCallback(() => {
    // Nếu có quyền 'all' trên module GRADE_INPUT_MODULE thì là admin/khảo thí
    return hasPermission('GRADE_INPUT_MODULE', 'all');
  }, [hasPermission]);

  const handleEditTXDK = async () => {
    if (!editTXDK) {
      // Giảng viên được phân công là đủ quyền nhập TX, ĐK
      const hasPermission = await checkPermissionForClass();
      if (!hasPermission) {
        return;
      }
    }
    setEditTXDK(v => !v);
  };

  const handleEditL1 = async () => {
    if (!editL1) {
      // Check quyền admin/khảo thí trước
      if (!canInputExamGrades()) {
        message.warning('Chỉ phòng Khảo thí mới có quyền nhập điểm thi!');
        return;
      }

      const hasPermission = await checkPermissionForClass();
      if (!hasPermission) {
        return;
      }
    }
    setEditL1(v => !v);
  };

  const handleEditL2 = async () => {
    if (!editL2) {
      // Check quyền admin/khảo thí trước
      if (!canInputExamGrades()) {
        message.warning('Chỉ phòng Khảo thí mới có quyền nhập điểm thi lại!');
        return;
      }

      const hasPermission = await checkPermissionForClass();
      if (!hasPermission) {
        return;
      }
    }
    setEditL2(v => !v);
  };

  const handleInputChange = (value: any, record: StudentGrade, field: string) => {
    // Nếu là cột điểm số, ép kiểu về số nếu có giá trị, còn lại là null
    let newValue = value;
    if (["DiemTX", "DiemDK", "DiemThiL1", "DiemThiL2"].includes(field)) {
      if (typeof value === 'string' && value.trim() === '') newValue = null;
      else if (typeof value === 'number') newValue = value;
      else if (!isNaN(Number(value))) newValue = Number(value);
      else newValue = null;
    }

    const newData = data.map(row => {
      if ((row.MaHS || row.MaSV) === (record.MaHS || record.MaSV)) {
        const updated = { ...row, [field]: newValue };
        const diemTBHeSo = tinhDiemTBHeSo(updated.DiemTX, updated.DiemDK);
        let diemTBMon = 0, quydoi = { chu: '', thang4: 0 };

        if (editL1 && typeof updated.DiemThiL1 === 'number') {
          diemTBMon = tinhDiemTBMon(diemTBHeSo, updated.DiemThiL1, updated.DiemThiL2);
          quydoi = diemToChuVaThang4(diemTBMon);
        } else if (editL2 && typeof updated.DiemThiL2 === 'number') {
          diemTBMon = tinhDiemTBMon(diemTBHeSo, updated.DiemThiL1, updated.DiemThiL2);
          quydoi = diemToChuVaThang4(diemTBMon);
        }

        // Sửa logic: chỉ set 'Học lại' nếu TB hệ số < 5
        let ghiChu = updated.GhiChu;
        if (diemTBHeSo < 5) ghiChu = 'Học lại';
        else if (ghiChu === 'Học lại') ghiChu = '';

        return {
          ...updated,
          DiemTB_HeSo: diemTBHeSo,
          DiemTB_Mon: diemTBMon,
          DiemChu: quydoi.chu,
          DiemThang4: quydoi.thang4,
          GhiChu: ghiChu
        };
      }
      return row;
    });
    setData(newData);
  };

  const handleSave = async () => {
    // Kiểm tra quyền trước khi lưu
    const hasPermission = await checkPermissionForClass();
    if (!hasPermission) {
      return;
    }

    setSaving(true);
    try {
      const headers = getAuthHeaders();
      console.log('[DEBUG] Headers for handleSave:', headers);

      await Promise.all(data.map(async (row) => {
        if (
          row.DiemTX !== null ||
          row.DiemDK !== null ||
          row.DiemThiL1 !== null ||
          row.DiemThiL2 !== null
        ) {
          const response = await fetch(buildAPIURL('/grades'), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
              LopHocPhanID: (row.LopHocPhanID || lopHocPhanId || '').toString().trim(),
              MaHS: (row.MaHS || row.MaSV || '').toString().trim(),
              DiemTX: row.DiemTX,
              DiemDK: row.DiemDK,
              DiemThiL1: row.DiemThiL1,
              DiemThiL2: row.DiemThiL2,
              GhiChu: row.GhiChu || ''
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }
      }));
      message.success('Lưu điểm thành công!');
      await fetchData();
      setEditTXDK(false); // Khóa lại ô TX, ĐK sau khi lưu
    } catch (err) {
      console.error('[DEBUG] Error saving grades:', err);
      message.error('Lưu điểm thất bại!');
    }
    setSaving(false);
  };

  const handleImportExcel = (file: File) => {
    importNhapDiemFromExcel(file, (importedList: any[]) => {
      const errors = validateImportedScores(importedList);
      if (errors.length) {
        message.error(errors.join('\n'));
        return;
      }

      // Ghép điểm vào data hiện tại theo MaHS
      const newData = data.map(row => {
        const found = importedList.find(i => (i.MaHS || '').toString().trim() === (row.MaHS || '').toString().trim());
        if (found) {
          const updated = { ...row, DiemTX: found.DiemTX, DiemDK: found.DiemDK };

          // Tính toán lại các điểm thành phần
          const diemTBHeSo = tinhDiemTBHeSo(updated.DiemTX, updated.DiemDK);

          let diemTBMon = updated.DiemTB_Mon;
          let quydoi = { chu: updated.DiemChu, thang4: updated.DiemThang4 };

          // Nếu có điểm thi L1 thì mới tính TB môn
          if (typeof updated.DiemThiL1 === 'number' && updated.DiemThiL1 !== null) {
            diemTBMon = tinhDiemTBMon(diemTBHeSo, updated.DiemThiL1, updated.DiemThiL2);
            quydoi = diemToChuVaThang4(diemTBMon);
          }

          // Tính lại ghi chú: chỉ set 'Học lại' nếu TB hệ số < 5
          let ghiChu = updated.GhiChu;
          if (diemTBHeSo < 5) ghiChu = 'Học lại';
          else if (ghiChu === 'Học lại') ghiChu = ''; // Clear nếu đã qua

          return {
            ...updated,
            DiemTB_HeSo: diemTBHeSo,
            DiemTB_Mon: diemTBMon,
            DiemChu: quydoi.chu,
            DiemThang4: quydoi.thang4,
            GhiChu: ghiChu
          };
        }
        return row;
      });
      setData(newData);
      message.success('Đã import điểm từ Excel!');
    });
    return false; // Ngăn upload mặc định
  };

  const handleExportTemplate = () => {
    if (!data || data.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }

    const filterInfo = {
      TenLopHP: tenLopHocPhan ? `${tenKhoaHoc} - ${tenLopHocPhan}` : (data[0]?.TenLopHP || lopHocPhanId),
      TenMonHoc: tenMonHoc || data[0]?.TenMonHoc || 'Tin học',
      TenGV: tenGV || (user as any)?.FullName || user?.fullName || user?.username || 'Giáo viên'
    };

    console.log('[DEBUG] Export template filterInfo:', filterInfo);
    console.log('[DEBUG] Current user:', user);

    // Format dữ liệu sinh viên với ngày sinh dd/MM/yyyy và đảm bảo MaHS có giá trị
    const formattedStudents = data.map(student => ({
      ...student,
      MaHS: student.MaHS || student.MaSV || '', // Đảm bảo MaHS luôn có giá trị
      NgaySinh: dayjs(student.NgaySinh).format('DD/MM/YYYY')
    }));

    exportNhapDiemTemplate({ filterInfo, students: formattedStudents });
  };

  const handleExportBangDiem = () => {
    if (!data || data.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }

    const filterInfo = {
      TenLopHP: tenLopHocPhan ? `${tenKhoaHoc} - ${tenLopHocPhan}` : (data[0]?.TenLopHP || lopHocPhanId),
      TenMonHoc: tenMonHoc || data[0]?.TenMonHoc || 'Tin học',
      TenGV: tenGV || (user as any)?.FullName || user?.fullName || user?.username || 'Giáo viên'
    };

    console.log('[DEBUG] Export bang diem filterInfo:', filterInfo);
    console.log('[DEBUG] Current user:', user);

    // Format dữ liệu sinh viên với ngày sinh dd/MM/yyyy và đảm bảo MaHS có giá trị
    const formattedStudents = data.map(student => ({
      ...student,
      MaHS: student.MaHS || student.MaSV || '', // Đảm bảo MaHS luôn có giá trị
      NgaySinh: dayjs(student.NgaySinh).format('DD/MM/YYYY')
    }));

    exportBangDiem({ filterInfo, students: formattedStudents });
  };

  const columns = [
    { title: 'Mã HS', dataIndex: 'MaHS', width: 100, render: (v: string, r: StudentGrade) => r.MaHS || r.MaSV },
    { title: 'Họ tên', dataIndex: 'HoTen', width: 180 },
    {
      title: 'Ngày sinh',
      dataIndex: 'NgaySinh',
      width: 120,
      render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : ''
    },
    {
      title: 'Điểm TX',
      dataIndex: 'DiemTX',
      width: 90,
      render: (v: number | null, r: StudentGrade, idx: number) => (
        <InputNumber
          min={0}
          max={10}
          value={v === null ? undefined : v}
          ref={el => (inputRefs.current[`DiemTX_${r.MaHS || r.MaSV}`] = el)}
          onChange={val => handleInputChange(val, r, 'DiemTX')}
          onKeyDown={e => handleInputKeyDown(e, idx, 'DiemTX')}
          disabled={!editTXDK}
        />
      )
    },
    {
      title: 'Điểm ĐK',
      dataIndex: 'DiemDK',
      width: 90,
      render: (v: number | null, r: StudentGrade, idx: number) => (
        <InputNumber
          min={0}
          max={10}
          value={v === null ? undefined : v}
          ref={el => (inputRefs.current[`DiemDK_${r.MaHS || r.MaSV}`] = el)}
          onChange={val => handleInputChange(val, r, 'DiemDK')}
          onKeyDown={e => handleInputKeyDown(e, idx, 'DiemDK')}
          disabled={!editTXDK}
        />
      )
    },
    {
      title: 'Điểm thi L1',
      dataIndex: 'DiemThiL1',
      width: 90,
      render: (v: number | null, r: StudentGrade, idx: number) => (
        <InputNumber
          min={0}
          max={10}
          value={v === null ? undefined : v}
          ref={el => (inputRefs.current[`DiemThiL1_${r.MaHS || r.MaSV}`] = el)}
          onChange={val => handleInputChange(val, r, 'DiemThiL1')}
          onKeyDown={e => handleInputKeyDown(e, idx, 'DiemThiL1')}
          disabled={!editL1}
        />
      )
    },
    {
      title: 'Điểm thi L2',
      dataIndex: 'DiemThiL2',
      width: 90,
      render: (v: number | null, r: StudentGrade, idx: number) => (
        <InputNumber
          min={0}
          max={10}
          value={v === null ? undefined : v}
          ref={el => (inputRefs.current[`DiemThiL2_${r.MaHS || r.MaSV}`] = el)}
          onChange={val => handleInputChange(val, r, 'DiemThiL2')}
          onKeyDown={e => handleInputKeyDown(e, idx, 'DiemThiL2')}
          disabled={!editL2}
        />
      )
    },
    { title: 'TB hệ số', dataIndex: 'DiemTB_HeSo', width: 90 },
    {
      title: 'TB môn',
      dataIndex: 'DiemTB_Mon',
      width: 90,
      render: (v: number, r: StudentGrade) => (r.DiemThiL1 !== null || r.DiemThiL2 !== null) ? v : ''
    },
    {
      title: 'Điểm chữ',
      dataIndex: 'DiemChu',
      width: 80,
      render: (v: string, r: StudentGrade) => (r.DiemThiL1 !== null || r.DiemThiL2 !== null) ? v : ''
    },
    {
      title: 'Thang 4',
      dataIndex: 'DiemThang4',
      width: 80,
      render: (v: number, r: StudentGrade) => (r.DiemThiL1 !== null || r.DiemThiL2 !== null) ? v : ''
    },
    {
      title: 'Ghi chú',
      dataIndex: 'GhiChu',
      width: 120,
      render: (v: string, r: StudentGrade, idx: number) => (
        <Input
          value={v}
          ref={el => (inputRefs.current[`GhiChu_${r.MaHS || r.MaSV}`] = el)}
          style={r.DiemTB_HeSo < 5 ? { background: '#ffd6d6', fontWeight: 'bold' } : {}}
          onChange={e => handleInputChange(e.target.value, r, 'GhiChu')}
          onKeyDown={e => handleInputKeyDown(e, idx, 'GhiChu')}
        />
      )
    },
  ];

  return (
    <Spin spinning={loading || saving} tip={saving ? 'Đang lưu...' : 'Đang tải...'}>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Tải lại
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={handleEditTXDK}
          disabled={false}
        >
          {editTXDK ? 'Đóng nhập điểm TX, ĐK' : 'Nhập điểm TX, ĐK'}
        </Button>
        <Button
          type="primary"
          icon={<FileDoneOutlined />}
          onClick={handleEditL1}
          disabled={!canInputExamGrades()}
        >
          {editL1 ? 'Đóng nhập điểm thi' : 'Nhập điểm thi'}
        </Button>
        <Button
          icon={<FileSyncOutlined />}
          onClick={handleEditL2}
          disabled={!canInputExamGrades()}
        >
          {editL2 ? 'Đóng nhập điểm thi lại' : 'Nhập điểm thi lại'}
        </Button>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExportTemplate}
        >
          Xuất DS nhập điểm
        </Button>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExportBangDiem}
        >
          Xuất bảng điểm
        </Button>
        <Upload
          beforeUpload={handleImportExcel}
          showUploadList={false}
          accept=".xlsx,.xls"
        >
          <Button icon={<UploadOutlined />}>Import điểm Excel</Button>
        </Upload>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{ x: 1200, y: 500 }}
        bordered
        size="small"
        rowKey={(record) => record.MaHS || record.MaSV || record.key}
        footer={() => (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={false} // Tạm thời bỏ qua kiểm tra quyền: !hasPermission('GRADE_INPUT_MODULE', 'edit')
          >
            Lưu điểm
          </Button>
        )}
      />
    </Spin>
  );
};

export default NhapDiemLopHocPhanPage;
